import { ThemeSelector } from './theme-selector/theme-selector-server';
import { FlagContent } from './language-selector/flag-content';
import {
  countryLanguages,
  LocaleDetails,
  LocaleValue,
  supportedLocales,
  getRegionSpecificDefaultHref,
} from 'shared/src/types/languages.type';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import { AppShellProfileButton } from './app-profile-button';
import { PriceRange } from 'shared/src/types/categories.type';
import { SiteWordmark } from './site-wordmark';
import { SearchToggleClient } from './search-toggle-client';
import { getSearchUiContent } from '@/lib/ui-content/search-ui-content';
// Admin-only: the dynamic() boundary lives in a client wrapper so the chunk is
// fetched only when the button renders (TopMenu is a server component, and a
// dynamic() here would ship the admin bundle to every visitor).
import { AdminToolsButtonLazy as AdminToolsButton } from '../admin/admin-tools-button-lazy';

const LanguageSelector = dynamic(() => import('./language-selector/language-selector').then((module) => module.LanguageSelector), {
  ssr: false,
  loading: () => null,
});
// Dev-only (see AUTH-001 below): the NODE_ENV guard is a compile-time constant,
// so webpack never parses the dead branch in production builds and the
// profile/sign-in bundle (incl. firebase/auth) never ships to visitors.
const ProfileButton =
  process.env.NODE_ENV !== 'production'
    ? dynamic(() => import('./profile-button').then((module) => module.ProfileButton), {
        ssr: false,
        loading: () => <div className="h-8 w-8" />,
      })
    : null;

const TopMenu = ({
  admin = false,
  hide = false,
  home,
  href,
  id,
  priceRange,
  locale,
  pageTitle,
  loadingScreen = false,
  companyName,
  noTitle = false,
  searchbar = true,
  pg18 = false,
  productTitles,
  hideLogo = false,
  /**
   * When set (e.g. product detail page), show Admin Tools using this URL for
   * server actions (category listing href), even if `searchbar` is false.
   */
  adminToolsPageHref,
  /** Bare bar (no background/border, tighter padding) — used on the main search bar page. */
  compactPadding = false,
}: {
  admin?: boolean;
  hide?: boolean;
  home?: string;
  href?: LocaleValue;
  id?: string;
  priceRange?: PriceRange;
  locale: LocaleDetails;
  pageTitle?: string;
  loadingScreen?: boolean;
  companyName?: string;
  noTitle?: boolean;
  searchbar?: boolean;
  pg18?: boolean;
  productTitles?: string[];
  hideLogo?: boolean;
  adminToolsPageHref?: string;
  compactPadding?: boolean;
}) => {
  if (href === undefined) {
    const locales = supportedLocales[locale.path];
    const map = locales?.map((langCode) => {
      return {
        [langCode.path]: `/${langCode.path}`,
      };
    });

    // Use shared function to get region-specific default href
    const defaultHref = getRegionSpecificDefaultHref(locale);

    href = map?.reduce((a, b) => ({ ...a, ...b }), {}) ?? defaultHref;
  }

  const defaultLocale = locale.default ? countryLanguages[locale.default] : undefined;
  const defaultLocalePath = defaultLocale?.path ? href?.[defaultLocale.path] : undefined;
  const adminToolsHref =
    (adminToolsPageHref && adminToolsPageHref.trim()) ||
    href?.[locale.path] ||
    '';
  const showAdminTools = admin && !loadingScreen && (searchbar || Boolean(adminToolsPageHref?.trim()));
  // Product / category pages: the wordmark on the bar, then search and
  // locale+theme in capsules on the same frost, without the bottom hairline.
  // The locale cluster stays pinned to the right when search opens. It takes the
  // third grid column explicitly: below `sm` the search cell is display:none, and
  // an auto-placed cluster would fall into the middle column beside the wordmark.
  const threeBox = Boolean(searchbar && !noTitle && !loadingScreen);
  const chromePill =
    'h-[2.625rem] rounded-full border border-hairline bg-white/70 backdrop-blur dark:bg-dark/70';
  const chromeCluster = (
    <div
      className={`z-50 flex items-center ${
        threeBox
          ? `col-start-3 justify-self-end shrink-0 ${chromePill} pl-4 pr-0`
          : compactPadding
            ? `ml-auto ${chromePill} pl-4 pr-0`
            : 'ml-auto'
      }`}
    >
      {href && !loadingScreen && (
        <LanguageSelector locale={locale} href={href} hide={hide} header={noTitle}>
          <FlagContent lang={locale} />
        </LanguageSelector>
      )}
      {/* Show profile button only in development */}
      {process.env.NODE_ENV !== 'production' && ProfileButton && (
        <>
          <ThemeSelector showRightBorder />
          <ProfileButton locale={locale} />
        </>
      )}
      {/* In production, hide profile button and adjust theme selector padding.
          Exception: the native app shell shows it (accounts ship in
          the apps) — AppShellProfileButton renders null on the web
          and only fetches the auth chunk inside the shell. */}
      {process.env.NODE_ENV === 'production' && (
        <>
          <ThemeSelector showRightBorder={false} />
          <AppShellProfileButton locale={locale} />
        </>
      )}
    </div>
  );

  return (
    <>
      {noTitle && !companyName && !searchbar && !hideLogo && (
        // <a
        //   id={`logo-inceptionai`}
        //   className={`top-0 z-[250] sm:z-[350] -mt-0.5 h-24 w-24 max-sm:mt-1.5 max-sm:h-[5.5rem] max-sm:w-[5.5rem] max-sm:animate-spin-slow sm:top-2 ${
        //     loadingScreen ? 'sm:animate-spin-10sec' : 'sm:hover:animate-spin-slow'
        //   } fixed left-0`}
        //   href={home ? home : `/${locale.path}`}
        // >
        //   <Image
        //     src="/images/logo-inceptionai.png"
        //     alt="Inception logo"
        //     fill
        //     // 96 CSS px → at 3x DPR Next picks 384w (not 256w), so the mark stays sharp
        //     // on retina without shipping the full 2K source.
        //     sizes="300px"
        //     className="dark:brightness-[0.95]"
        //     priority
        //     quality={100}
        //   />
        // </a>
        <>
        </>
      )}
      {showAdminTools ? (
        <AdminToolsButton locale={locale} href={adminToolsHref} defaultLocalePath={defaultLocalePath} pg18={pg18} />
      ) : null}
      {/* data-top-bar: menus portaled out of here (LanguageSelector) measure
          this element to hang themselves off the bar's bottom edge. */}
      {/* Frosted-glass bar, only where the wordmark sits top-left: always on pages
          that render it (product, category), and on the search page once a
          conversation has docked it (DockedWordmark sets `data-docked` on this
          element). On the bare hero the bar stays transparent. saturate(180%) +
          blur(20px) is Apple's recipe; dark mode drops the saturate (our page is
          navy, and saturating it paints the empty bar a visible band). */}
      <div
        data-top-bar
        className={`sticky top-0 z-50 w-full flex-none max-sm:px-1 sm:px-2 lg:z-50 safe-top ${
          noTitle
            ? 'data-[docked]:bg-white/70 data-[docked]:backdrop-blur-[20px] data-[docked]:backdrop-saturate-[1.8] dark:data-[docked]:bg-dark/70 dark:data-[docked]:backdrop-saturate-100'
            : 'bg-white/70 backdrop-blur-[20px] backdrop-saturate-[1.8] dark:bg-dark/70 dark:backdrop-saturate-100'
        }`}
        style={{ WebkitTransform: 'translate3d(0,0,0)' }}
      >
        <div className="max-w-8xl mx-auto">
          <div className={`my-auto pb-[0.35rem] pt-[0.925rem]`}>
            <div
              className={
                threeBox
                  ? 'grid grid-cols-[1fr_auto_1fr] items-center gap-2 sm:gap-3'
                  : 'flex items-center'
              }
            >
              {!noTitle ? (
                <a
                  href={`/${locale.path}`}
                  translate="no"
                  // Three-box (product pages): the wordmark keeps the capsule's height
                  // and inner padding, so it lines up with the search and locale
                  // cluster and does not move, but draws neither outline nor fill —
                  // the bar already carries the same frost, and a second layer of it
                  // would show as a faint capsule once content scrolls underneath.
                  // Other pages keep the unboxed mark — left inset lands its
                  // ink the same distance from the viewport edge as the right-hand
                  // controls' ink (bar px-1/px-2 + ~1rem inside the trailing button).
                  className={
                    threeBox
                      ? 'relative z-50 col-start-1 justify-self-start flex h-[2.625rem] shrink-0 cursor-pointer items-center px-3.5 sm:px-4'
                      : 'relative z-50 ml-3 mr-auto flex shrink-0 cursor-pointer items-center max-xxs:ml-2 sm:ml-6'
                  }
                >
                  <SiteWordmark />
                  {pg18 && (
                    <span className="absolute -right-8 -top-2 sm:-right-10 text-xs font-bold px-2 py-1 rounded-md rotate-[30deg] shadow-lg dark:shadow text-red-600 dark:text-red-700">
                      18+
                    </span>
                  )}
                </a>
              ) : (
                // Search-page variant: the hero carries the wordmark instead. This
                // empty slot sits exactly where the link above would (same
                // classes minus `mr-auto`, which would split the row's free space
                // with the right cluster's `ml-auto` and drag it off the edge), so
                // once a conversation starts the hero wordmark can dock into it —
                // see docked-wordmark.tsx (portal target).
                <span
                  data-wordmark-slot
                  className="relative z-50 ml-3 flex shrink-0 items-center max-xxs:ml-2 sm:ml-6"
                />
              )}
              {companyName ? (
                <a
                  className="first-letter: fixed left-[calc(50vw-12rem)] justify-center text-center font-mono tracking-tighter max-md:tracking-[-0.1em] max-sm:left-0 max-sm:text-xl max-xs:text-base max-xxs:text-sm sm:text-2xl"
                  translate="no"
                  href={home ? home : `/${locale.path}`}
                >
                  {companyName?.toUpperCase().replace(/(?<=\w)(?=\w)/g, ' ') ?? ''}&nbsp;&nbsp;&nbsp;N E W S
                </a>
              ) : null}
              {searchbar && !loadingScreen ? (
                <SearchToggleClient
                  locale={locale}
                  priceRange={priceRange}
                  productTitles={productTitles}
                  pageTitle={pageTitle}
                  embedded={threeBox}
                  placeholder={getSearchUiContent(locale).searchPlaceholderProductPage}
                />
              ) : null}
              {chromeCluster}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TopMenu;
